/**
 * LotSmith — generic legal-doc renderer.
 *
 * Each /terms /privacy /refund /disclaimer route imports the matching
 * markdown file from docs/legal at build time (Vite ?raw) and feeds it
 * to a tiny inline markdown→HTML converter so we don't pull a full
 * library for these four static pages.
 *
 * The markdown source lives in docs/legal/* so the founder can review
 * legal updates in normal PR diffs without touching component code.
 */
import { Link } from "react-router-dom";
import { useMemo } from "react";

import disclaimerMd from "../../../../docs/legal/disclaimer.md?raw";
import termsMd from "../../../../docs/legal/terms.md?raw";
import privacyMd from "../../../../docs/legal/privacy.md?raw";
import refundMd from "../../../../docs/legal/refund.md?raw";

type LegalDoc = "terms" | "privacy" | "refund" | "disclaimer";

const SOURCES: Record<LegalDoc, string> = {
  terms: termsMd,
  privacy: privacyMd,
  refund: refundMd,
  disclaimer: disclaimerMd,
};

interface Props { doc: LegalDoc; }

export function LegalPage({ doc }: Props) {
  const html = useMemo(() => mdToHtml(SOURCES[doc]), [doc]);
  return (
    <div className="legal">
      <header className="nav">
        <div className="nav__inner">
          <Link to="/" className="nav__brand">LotSmith</Link>
          <nav className="nav__links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/login" className="btn btn--ghost">Sign in</Link>
          </nav>
        </div>
      </header>
      <article
        className="legal__doc"
        // eslint-disable-next-line react/no-danger -- mdToHtml output is from
        // our own static markdown files; nothing user-controlled lands here.
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <footer className="legal__foot">
        <Link to="/terms">Terms</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/refund">Refund</Link>
        <Link to="/disclaimer">Disclaimer</Link>
      </footer>
    </div>
  );
}

// Minimal markdown→HTML converter for our docs only. Handles:
// - # / ## / ### headings
// - paragraphs separated by blank lines
// - **bold** and *italic*
// - inline `code`
// - fenced code blocks ```...```
// - bullet lists (`- ` lines) and numbered lists (`1. ` lines)
// - tables (| col | col | … | with separator row)
// - links [text](url)
// - horizontal rules ---
//
// We intentionally don't handle images, html passthrough, or footnotes —
// our docs don't use them.
function mdToHtml(src: string): string {
  // Strip leading top-of-file `# Title` (we render it as the page title in
  // CSS) and trailing whitespace.
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";

    if (line.startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").startsWith("```")) {
        buf.push(escapeHtml(lines[i] ?? ""));
        i++;
      }
      i++; // skip closing fence
      out.push(`<pre><code>${buf.join("\n")}</code></pre>`);
      continue;
    }

    if (line.startsWith("### ")) {
      out.push(`<h3>${inline(line.slice(4))}</h3>`);
      i++; continue;
    }
    if (line.startsWith("## ")) {
      out.push(`<h2>${inline(line.slice(3))}</h2>`);
      i++; continue;
    }
    if (line.startsWith("# ")) {
      out.push(`<h1>${inline(line.slice(2))}</h1>`);
      i++; continue;
    }

    if (line.trim() === "---") {
      out.push("<hr/>");
      i++; continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i] ?? "")) {
        items.push(`<li>${inline((lines[i] ?? "").replace(/^\s*[-*]\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i] ?? "")) {
        items.push(`<li>${inline((lines[i] ?? "").replace(/^\s*\d+\.\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join("")}</ol>`);
      continue;
    }

    if (line.startsWith("|") && (lines[i + 1] ?? "").includes("---")) {
      const header = parseRow(line);
      const rows: string[][] = [];
      i += 2; // skip header + separator
      while (i < lines.length && (lines[i] ?? "").startsWith("|")) {
        rows.push(parseRow(lines[i] ?? ""));
        i++;
      }
      out.push(
        "<table>" +
        `<thead><tr>${header.map((h) => `<th>${inline(h)}</th>`).join("")}</tr></thead>` +
        `<tbody>${rows.map((r) =>
          `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join("")}</tr>`,
        ).join("")}</tbody>` +
        "</table>",
      );
      continue;
    }

    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph — accumulate until blank line.
    const buf: string[] = [line];
    i++;
    while (i < lines.length && (lines[i] ?? "").trim() !== "" && !isBlockStart(lines[i] ?? "")) {
      buf.push(lines[i] ?? "");
      i++;
    }
    out.push(`<p>${inline(buf.join(" "))}</p>`);
  }
  return out.join("\n");
}

function isBlockStart(line: string): boolean {
  return (
    line.startsWith("#") ||
    line.startsWith("```") ||
    line.startsWith("|") ||
    /^\s*[-*]\s+/.test(line) ||
    /^\s*\d+\.\s+/.test(line) ||
    line.trim() === "---"
  );
}

function parseRow(line: string): string[] {
  return line.split("|").slice(1, -1).map((c) => c.trim());
}

function inline(s: string): string {
  return escapeHtml(s)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g,
      (_m, text, href) => `<a href="${href}">${text}</a>`);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
